/**
 * OrderDetailsModal Component
 * 
 * Shows full order details with accept/decline actions
 */

import { useState } from 'react';
import { Order } from '@/store/orders';
import { formatCurrency, formatDistance } from '@/lib/utils';
import { logger } from '@/lib/logger';

interface OrderDetailsModalProps {
  order: Order;
  isOpen: boolean;
  onClose: () => void;
  onAccept: (orderId: string) => Promise<void>;
  onDecline: (orderId: string) => void;
  isAccepting: boolean;
}

export default function OrderDetailsModal({
  order,
  isOpen,
  onClose,
  onAccept,
  onDecline,
  isAccepting,
}: OrderDetailsModalProps) {
  const [isDeclining, setIsDeclining] = useState(false);

  if (!isOpen) return null;

  const handleAccept = async () => {
    try {
      await onAccept(order.id);
      onClose();
    } catch (error) {
      logger.error('Failed to accept order', error as Error);
    }
  };

  const handleDecline = () => {
    setIsDeclining(true);
    setTimeout(() => {
      onDecline(order.id);
      onClose();
      setIsDeclining(false);
    }, 300);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-xl animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Order Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Earnings */}
          <div className="text-center py-4 bg-primary/10 rounded-lg">
            <div className="text-sm text-muted-foreground mb-1">You'll earn</div>
            <div className="text-3xl font-bold text-primary">
              {formatCurrency(order.deliveryFee)}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {formatDistance(order.distance)} delivery
            </div>
          </div>

          {/* Vendor Info */}
          <div>
            <h3 className="font-semibold mb-2">Pickup from</h3>
            <div className="bg-muted p-3 rounded-lg">
              <div className="font-medium">{order.vendor.name}</div>
              <div className="text-sm text-muted-foreground">{order.vendor.address}</div>
            </div>
          </div>

          {/* Items */}
          <div>
            <h3 className="font-semibold mb-2">Order Items ({order.items.length})</h3>
            <div className="space-y-2">
              {order.items.map((item) => (
                <div key={item.id} className="flex justify-between bg-muted p-3 rounded-lg">
                  <div>
                    <div className="font-medium">{item.name}</div>
                    <div className="text-sm text-muted-foreground">Qty: {item.quantity}</div>
                  </div>
                  <div className="font-medium">{formatCurrency(item.price * item.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t flex justify-between font-semibold">
              <span>Total Amount</span>
              <span>{formatCurrency(order.totalAmount)}</span>
            </div>
          </div>

          {/* Delivery Info */}
          <div>
            <h3 className="font-semibold mb-2">Deliver to</h3>
            <div className="bg-muted p-3 rounded-lg">
              <div className="font-medium">{order.customer.name}</div>
              <div className="text-sm text-muted-foreground">{order.delivery.address}</div>
              {order.delivery.instructions && (
                <div className="mt-2 text-sm">
                  <span className="font-medium">Instructions: </span>
                  {order.delivery.instructions}
                </div>
              )}
            </div>
          </div>

          {/* Payment Method */}
          <div className="flex items-center justify-between py-3 border-t">
            <span className="text-muted-foreground">Payment Method</span>
            <span className={`px-3 py-1 rounded font-medium ${
              order.paymentMethod === 'cod'
                ? 'bg-orange-100 text-orange-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {order.paymentMethod.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-card border-t p-4 flex gap-3">
          <button
            onClick={handleDecline}
            disabled={isAccepting || isDeclining}
            className="flex-1 py-3 px-4 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/80 transition-colors disabled:opacity-50"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            disabled={isAccepting || isDeclining}
            className="flex-1 py-3 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isAccepting ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Accepting...
              </>
            ) : (
              'Accept Order'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}


















